import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useT } from '../i18n';
import ShopProfileFields from './ShopProfileFields';
import { emptyShopProfile, validateShopProfile } from '../constants/shop';
import { getUserDocument, updateShopProfile } from '../firebase/userService';

// ── SHOP PROFILE (Settings) ──────────────────────────────────────────────────
// The post-signup home for the same fields the registration step collects, and
// the ONLY place a Google sign-up ever fills them in — OAuth skips the details
// step, so an account that chose "shop" on /account-type arrives here with a
// profile that is entirely empty. Reuses ShopProfileFields verbatim, so what is
// accepted here and what was accepted at signup can never diverge.
//
// Rendered only for accounts that already ARE shops (see Settings.jsx), exactly
// as the designer card is — becoming one is a decision made at signup.
export default function ShopSettingsCard() {
  const { user, refreshProfile, syncAuthName } = useAuth();
  const { t } = useT();

  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(() => ({ ...emptyShopProfile(), avatarUrl: '' }));
  const [errors, setErrors]   = useState({});
  const [saving, setSaving]   = useState(false);
  const [toast, setToast]     = useState(null);

  // Load once per user. The saved profile is merged OVER the empty shape rather
  // than replacing it, so a document written before a field existed still yields
  // a complete form — otherwise a missing key turns a controlled input into an
  // uncontrolled one the first time it is typed in.
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!user?.uid) return;
      try {
        const doc = await getUserDocument(user.uid);
        if (!alive) return;
        const saved = doc?.shop || {};
        setProfile({
          ...emptyShopProfile(doc?.fullName || user.displayName || ''),
          ...saved,
          // The serializer omits empty channels, so the saved map holds only the
          // filled-in ones; the form needs a key for every input it renders.
          socials: { ...emptyShopProfile().socials, ...(saved.socials || {}) },
          // `fullName` is the authoritative name — updateShopProfile writes it
          // to both places, and it is the one every other screen reads.
          name: doc?.fullName || saved.name || '',
          avatarUrl: doc?.avatarUrl || '',
        });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [user?.uid, user?.displayName]);

  const showToast = useCallback((msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  async function handleSave(e) {
    e.preventDefault();
    const errs = validateShopProfile(profile);
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setSaving(true);
    try {
      await updateShopProfile(user.uid, profile, profile.avatarUrl);
      // Keep Firebase Auth in step: the header reads displayName from there, so
      // without this the name saved here would not appear until re-login.
      await syncAuthName(profile.name);
      // Repull the shared profile so the header avatar/name update without a
      // reload.
      await refreshProfile();
      showToast(t('shop_saved'));
    } catch (err) {
      showToast(err?.message || 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return null;

  return (
    <div className="set-card" style={{ marginTop: 20 }}>
      <div className="set-identity-name" style={{ marginBottom: 4 }}>{t('shop_section_title')}</div>
      <p className="af-subtitle" style={{ margin: '0 0 6px' }}>{t('shop_section_sub')}</p>
      {/* The account email — a shop does not see the account card above (it
          edited the same name field), so it surfaces here instead. */}
      {user?.email && <p className="dash-user-email" style={{ margin: '0 0 18px' }}>{user.email}</p>}

      <form onSubmit={handleSave} noValidate>
        <ShopProfileFields
          value={profile}
          onChange={p => { setProfile(p); setErrors({}); }}
          errors={errors}
          disabled={saving}
          roomy
        />
        {/* Button and confirmation centred together, so the message sits
            directly under the button that caused it rather than a full form's
            scroll away at the top of the card. */}
        <div className="ds-actions">
          <button className="af-btn set-save-btn" type="submit" disabled={saving}>
            {saving && <span className="af-spinner" />}
            {saving ? t('set_saving') : t('shop_save')}
          </button>

          {toast && (
            <div className={`af-toast af-toast--${toast.type} ds-toast`} role="status" aria-live="polite">
              <span className="af-toast__icon">{toast.type === 'error' ? '⚠' : '✓'}</span>
              {toast.msg}
            </div>
          )}
        </div>
      </form>
    </div>
  );
}
